/**
 * External link-out helpers - Trade Loop v2.0 (Card 18, tasks A and C)
 *
 * Two operations the native paywall needs, and one rule that governs both: NOTHING IN HERE MAY
 * EVER NAVIGATE THE WEBVIEW TO A PAYMENT PAGE. A bundled native build that assigns
 * `window.location.href = <stripe url>` has no browser chrome and no way back, and is precisely
 * the "app uses its own mechanism to unlock content" shape Apple 3.1.1 and Google's billing
 * policy both police. Every function here either escapes to a real browser or does nothing.
 *
 * ## Why `window.open(url, '_blank')` and not `@capacitor/browser`
 *
 * `@capacitor/browser` opens SFSafariViewController on iOS, which has its own cookie jar. A user
 * sent there arrives signed out of gotradeloop.com, so checkout looks broken at the exact moment
 * they were about to pay. It is the wrong tool for this and is deliberately not a dependency.
 *
 * The system default browser is reached through Capacitor's own WebView delegate instead. On iOS
 * a `_blank` open with no window created lands in `WebViewDelegationHandler.webView(_:
 * createWebViewWith:for:windowFeatures:)`, whose entire body is `UIApplication.shared.open(url)`
 * — the OS default browser, cookie jar included
 * (node_modules/@capacitor/ios/Capacitor/Capacitor/WebViewDelegationHandler.swift:333-338).
 * That holds in both build flavors: bundled builds also hit the off-origin branch of
 * `decidePolicyFor` (same file, line 111), and the beta remote-URL wrapper reaches
 * `createWebViewWith` regardless of `server.url`.
 *
 * WKWebView only calls that delegate for a user-gesture-initiated open, so `openInSystemBrowser`
 * must be called synchronously from a click handler. Anything it is given must already be
 * resolved — await the token BEFORE the tap, or the gesture is gone by the time this runs.
 *
 * ## Failure is not a fallback to the WebView
 *
 * If the open is refused, this returns false and the caller shows the copy affordance. It does
 * NOT fall back to `window.location.assign`: in the `CAP_ENV=beta` wrapper `server.url` is
 * gotradeloop.com, so a same-host assign is an application navigation and would load checkout
 * inside the WebView — the one outcome this whole card exists to prevent.
 */

import { isNative } from '@/utils/platform';

/**
 * Open `url` in the user's default browser, outside the app.
 *
 * Returns false when the open was refused (blocked, or no delegate handled it). Callers must
 * treat false as "show the copyable URL", never as "navigate here instead".
 */
export function openInSystemBrowser(url: string): boolean {
  try {
    // 'noopener' matters on web, where this is a plain new tab: without it the opened page gets
    // a live `window.opener` handle back into the app.
    const opened = window.open(url, '_blank', 'noopener,noreferrer');

    // On native, Capacitor's delegate opens the OS browser and returns null by design, so a null
    // return is success there and a popup block on web. The two are indistinguishable from here,
    // which is why native reports success and web checks the handle.
    if (isNative) return true;
    return opened !== null;
  } catch (error) {
    console.error('[LinkOut] Failed to open the system browser:', error);
    return false;
  }
}

/**
 * Copy `text` to the clipboard.
 *
 * The Android paywall pairs this with the URL rendered as selectable text, so a silent failure
 * is recoverable rather than a dead end — which is the reason the caller must surface the
 * result. WebView clipboard access needs a secure context and a user gesture, so like
 * `openInSystemBrowser` this belongs in a click handler.
 *
 * `document.execCommand('copy')` is the fallback for WebViews where `navigator.clipboard` is
 * absent. It is deprecated on the web and still the only thing that works in some Android
 * WebView configurations.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    console.warn('[LinkOut] navigator.clipboard failed, trying execCommand:', error);
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    // Off-screen rather than hidden: a `display: none` element cannot be selected.
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch (error) {
    console.error('[LinkOut] Clipboard copy failed entirely:', error);
    return false;
  }
}
